// <s-review-filter>: instant search, star-rating dropdown, and a view dropdown (with media / highest / lowest / most recent) for the reviews drawer.
class SReviewFilter extends HTMLElement {
  connectedCallback() {
    this.search = this.querySelector('[data-review-search]');
    this.empty = this.querySelector('[data-review-empty]');
    this.list = this.querySelector('[data-review-item]')?.parentElement;
    this.items = Array.from(this.querySelectorAll('[data-review-item]'));
    this.items.forEach((item, i) => (item.dataset.index = i));
    this.state = { rating: '', view: '' };

    const run = () => this.apply();
    this.search?.addEventListener('input', run);
    this.search?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); run(); }
    });
    this.querySelector('[data-review-search-button]')?.addEventListener('click', run);

    this.clear = this.querySelector('[data-review-clear]');
    this.clear?.addEventListener('click', () => this.reset());
    this.dropdowns = Array.from(this.querySelectorAll('[data-review-dd]'));
    this.dropdowns.forEach((dd) => {
      const toggle = dd.querySelector('[data-dd-toggle]');
      const menu = dd.querySelector('[data-dd-menu]');
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const willOpen = !menu.hasAttribute('data-open');
        this.closeAll();
        if (willOpen) {
          menu.setAttribute('data-open', '');
          Object.assign(menu.style, { opacity: '1', transform: 'none', pointerEvents: 'auto', visibility: 'visible', transitionDelay: '0s' });
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
      menu.addEventListener('click', (e) => {
        const option = e.target.closest('[role="option"]');
        if (!option) return;
        const key = dd.dataset.reviewDd;
        const same = this.state[key] === option.dataset.value;
        // Clicking the active view option again clears it.
        this.state[key] = key === 'view' && same ? '' : option.dataset.value;
        menu.querySelectorAll('[role="option"]').forEach((o) => {
          const on = o.dataset.value === this.state[key];
          o.setAttribute('aria-selected', String(on));
          o.style.fontWeight = on ? '600' : '';
        });
        this.closeAll();
        toggle.focus();
        const chosen = menu.querySelector('[role="option"][aria-selected="true"]');
        dd.querySelector('[data-dd-label]').textContent = this.state[key] && chosen ? chosen.dataset.label : dd.dataset.defaultLabel;
        this.apply();
      });
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target) || !e.target.closest('[data-review-dd]')) this.closeAll();
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.querySelector('[data-dd-menu][data-open]')) {
        e.stopPropagation();
        e.preventDefault();
        this.closeAll();
      }
    });
  }

  reset() {
    this.state = { rating: '', view: '' };
    if (this.search) this.search.value = '';
    this.dropdowns.forEach((dd) => {
      dd.querySelector('[data-dd-label]').textContent = dd.dataset.defaultLabel;
      dd.querySelectorAll('[role="option"]').forEach((o) => {
        const on = dd.dataset.reviewDd === 'rating' && o.dataset.value === '';
        o.setAttribute('aria-selected', String(on));
        o.style.fontWeight = on ? '600' : '';
      });
    });
    this.closeAll();
    this.apply();
    this.search?.focus();
  }

  closeAll() {
    this.dropdowns?.forEach((dd) => {
      const menu = dd.querySelector('[data-dd-menu]');
      menu.removeAttribute('data-open');
      Object.assign(menu.style, { opacity: '0', transform: 'translateY(-4px)', pointerEvents: 'none', visibility: 'hidden', transitionDelay: '0s, 0s, .3s' });
      dd.querySelector('[data-dd-toggle]').setAttribute('aria-expanded', 'false');
    });
  }

  apply() {
    const q = (this.search?.value || '').trim().toLowerCase();
    const { rating, view } = this.state;
    let shown = 0;
    for (const item of this.items) {
      const ok = (!q || item.dataset.text.includes(q)) && (!rating || item.dataset.rating === rating) && (view !== 'media' || item.dataset.media === '1');
      item.hidden = !ok;
      if (ok) shown++;
    }
    if (this.empty) this.empty.hidden = shown > 0;
    if (this.clear) this.clear.hidden = !(q || rating || view);

    const by = {
      highest: (a, b) => b.dataset.rating - a.dataset.rating || b.dataset.date - a.dataset.date,
      lowest: (a, b) => a.dataset.rating - b.dataset.rating || b.dataset.date - a.dataset.date,
      recent: (a, b) => b.dataset.date - a.dataset.date,
    }[view] || ((a, b) => a.dataset.index - b.dataset.index);
    if (this.list) this.list.append(...[...this.items].sort(by));
  }
}
if (!customElements.get('s-review-filter')) customElements.define('s-review-filter', SReviewFilter);

// Laptop/tablet: slide the grey search/filter panel out from behind the reviews panel 0.8s after the drawer opens.
const reviewDialog = document.getElementById('product-reviews');
const reviewSide = reviewDialog && reviewDialog.querySelector('[data-review-side]');
if (reviewDialog && reviewSide) {
  let timer = null;
  new MutationObserver(() => {
    clearTimeout(timer);
    if (reviewDialog.open) {
      reviewSide.removeAttribute('data-in');
      timer = setTimeout(() => reviewSide.setAttribute('data-in', ''), 800);
    } else {
      reviewSide.removeAttribute('data-in');
    }
  }).observe(reviewDialog, { attributes: true, attributeFilter: ['open'] });
}

// Phones: keep only the filter row visible when the grey panel pins to the top of the drawer.
if (reviewDialog && reviewSide) {
  const filters = reviewSide.querySelector('[data-review-filters]');
  const phone = window.matchMedia('(max-width: 47.99rem)');
  const setStickyTop = () => {
    if (!filters) return;
    if (!phone.matches) {
      reviewSide.style.removeProperty('--review-sticky-top');
      return;
    }
    const offset = Math.max(filters.offsetTop - 16, 0);
    reviewSide.style.setProperty('--review-sticky-top', `-${offset}px`);
  };
  new MutationObserver(() => { if (reviewDialog.open) requestAnimationFrame(setStickyTop); }).observe(reviewDialog, { attributes: true, attributeFilter: ['open'] });
  window.addEventListener('resize', setStickyTop);
  phone.addEventListener?.('change', setStickyTop);
}
